import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormProvider, useForm } from "react-hook-form";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericDatePicker from "@/components/forms/inputs/genericDatePicker";
import GenericInputField from "@/components/forms/inputs/genericInputField";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/forms/userSettingsForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  userInfos?: any;
}

const UserSettingsForm: FC<Props> = ({ userInfos }) => {
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const methods = useForm<any>({
    mode: "onChange",
    shouldFocusError: false,
  });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({ fieldOrder: ["birth_date", "phone"] });

  const { t } = useTranslation();

  useEffect(() => {
    methods.setValue("birth_date", userInfos?.person?.birth_date);
    methods.setValue("phone", userInfos?.person?.phone);
  }, [userInfos]);

  const onSubmit = async (data: any) => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    api.genericApiRequest({
      entity: "user",
      method: "put",
      entityId: userInfos.user_id,
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setSuccessForNotification(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: data,
      config: config,
    });
  };

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t("personal_settings")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit, (errors) => {
                focusFirstInvalidField(errors);
              })}
              className={styles.form}
              noValidate
            >
              <GenericControllerWithInput
                name="birth_date"
                control={methods.control}
                renderInput={({ value }) => (
                  <GenericDatePicker
                    validationTargetRef={registerValidationTarget("birth_date")}
                    name="birth_date"
                    label={t("birthdate")}
                    predefinedvalue={value}
                    hideError={true}
                  />
                )}
              />
              <GenericControllerWithInput
                name="phone"
                control={methods.control}
                renderInput={({ value }) => (
                  <GenericInputField
                    validationTargetRef={registerValidationTarget("phone")}
                    predefinedvalue={value}
                    name="phone"
                    label={t("phone")}
                    inputType="tel"
                  />
                )}
              />

              <GenericButton className={styles.submitButton} type="submit">
                {t("save")}
              </GenericButton>
            </form>
          </FormProvider>
        </AccordionDetails>
      </Accordion>
      {errorModal}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default UserSettingsForm;
